import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft as ArrowLeft, FiEdit2 as Edit, FiSave as Save, FiX as X, FiTrash2 as Trash2, FiCopy as Copy, FiBarChart2 as BarChart3, FiCheckCircle as TestTube, FiCheckCircle as CheckCircle, FiDownload as Download, FiArrowUpCircle as ArrowUpCircle, FiRotateCcw as RotateCcw, FiSearch as Search, FiExternalLink as ExternalLink, FiClock as Clock, FiDollarSign as DollarSign, FiShield as Shield, FiInfo as Info, FiPlus as Plus, FiClock as History, FiSend as Rocket, FiAlertTriangle as AlertTriangle, FiActivity, FiCode } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { usePrompt, usePromptAnalytics } from '../../../../hooks/usePrompt';
import { clonePrompt, promotePrompt, rollbackPrompt, getTraces, getPromotionPolicies, getPrompts, getApprovals, getReleaseHistory, listEnvironments, assignVersionLabel, getPromptLabels, renamePrompt, getEnvironments } from '../../../../services/aiOperationsApi';
import PromptEditor from '../components/prompts/PromptEditor';
import PromotionModal from '../components/prompts/PromotionModal';
import RollbackModal from '../components/prompts/RollbackModal';
import TestPromptModal from '../components/prompts/TestPromptModal';
import { VersionComparisonSection } from '../../components/evaluations';
import { parseTemplate } from '../../../../utils/templateParser';
import BedrockSyncStatus, { SyncStatusBadge } from '../components/prompts/BedrockSyncStatus';
import ProjectAssignmentSelect from '../../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../../components/WorkloadAssignmentSelect';
import { assignPromptProject, assignPromptWorkload } from '../../../../services/aiOperationsApi';

/**
 * Format a user identifier for display.
 * Handles various formats: email, UUID, username
 */
const formatUserName = (userId: string) => {
  if (!userId) return 'System';

  // If it's an email, show the part before @
  if (userId.includes('@')) {
    return userId.split('@')[0];
  }

  // If it looks like a UUID (contains hyphens and is 36+ chars), truncate it
  if (userId.includes('-') && userId.length >= 32) {
    return userId.substring(0, 8) + '...';
  }

  // Otherwise return as-is (it's probably already a username)
  return userId;
};

const PromptDetailPage = () => {
  const { promptId } = useParams();
  const navigate = useNavigate();
  const { prompt, loading, error, saving, update, remove, refresh } = usePrompt(promptId);
  const { analytics } = usePromptAnalytics(promptId);

  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<Record<string, any>>({});
  const [showTestModal, setShowTestModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promotionResult, setPromotionResult] = useState<any>(null);
  const [rollingBack, setRollingBack] = useState(false);

  // Related traces state
  const [relatedTraces, setRelatedTraces] = useState<any[]>([]);
  const [tracesLoading, setTracesLoading] = useState(false);
  const [tracesError, setTracesError] = useState<string | null>(null);
  const [traceFilters, setTraceFilters] = useState({
    version: 'all',      // 'all' | number
    environment: 'all'   // 'all' | 'development' | 'staging' | 'production'
  });

  // Applicable policy state
  const [applicablePolicy, setApplicablePolicy] = useState<any>(null);
  const [policyLoading, setPolicyLoading] = useState(false);

  // Pending approval state
  const [pendingApproval, setPendingApproval] = useState<any>(null);

  // Release history state
  const [releaseHistory, setReleaseHistory] = useState<any[]>([]);
  const [releaseHistoryLoading, setReleaseHistoryLoading] = useState(false);

  // Clone dialog state
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [cloneError, setCloneError] = useState('');
  const [cloning, setCloning] = useState(false);

  // Version labels state
  const [versionLabels, setVersionLabels] = useState<any[]>([]);

  // Rename dialog state
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Available versions for comparison
  const [availableVersions, setAvailableVersions] = useState<any[]>([]);
  // Full prompt objects for all versions (for version history links)
  const [versionPrompts, setVersionPrompts] = useState<any[]>([]);
  // Dynamic environments
  const [environments, setEnvironments] = useState<string[]>(['development', 'staging', 'production']);
  // Promotion chain and environment definitions (from /environments API)
  const [promotionChain, setPromotionChain] = useState<any>(null);
  const [environmentDefs, setEnvironmentDefs] = useState<any>(null);

  // Bedrock sync ref and status
  const bedrockSyncRef = useRef<any>(null);
  const [bedrockSyncStatus, setBedrockSyncStatus] = useState('not_linked');

  // Helper to safely parse JSON or return array/object if already parsed
  const safeParse = <T,>(value: unknown, defaultValue: T): T => {
    if (value == null) return defaultValue;
    if (typeof value === 'object') return value as T; // Already parsed
    try {
      return JSON.parse(value as string);
    } catch {
      return defaultValue;
    }
  };

  // Initialize edited data when prompt loads
  React.useEffect(() => {
    if (prompt && !isEditing) {
      setEditedData({
        template: prompt.template,
        description: prompt.description || '',
        tags: safeParse(prompt.tags, []),
        labels: safeParse(prompt.labels, []),
        config: safeParse(prompt.config, {})
      });
    }
  }, [prompt, isEditing]);

  // Load related traces when prompt loads or filters change
  useEffect(() => {
    const loadRelatedTraces = async () => {
      if (!prompt?.name) return;

      setTracesLoading(true);
      setTracesError(null);
      try {
        const data = await getTraces({
          promptName: prompt.name,
          promptVersion: traceFilters.version === 'all' ? undefined : Number(traceFilters.version),
          environment: traceFilters.environment === 'all' ? undefined : traceFilters.environment,
          limit: 20
        });
        setRelatedTraces(data.data || []);
      } catch (err: unknown) {
        console.error('Error loading related traces:', err);
        setTracesError(err instanceof Error ? err.message : String(err));
      } finally {
        setTracesLoading(false);
      }
    };

    loadRelatedTraces();
  }, [prompt?.name, traceFilters]);

  // Load applicable promotion policy when prompt loads
  useEffect(() => {
    const loadApplicablePolicy = async () => {
      if (!prompt?.name) return;

      setPolicyLoading(true);
      try {
        const data = await getPromotionPolicies();
        const policies = data.data || [];

        // Find applicable policy: exact match on prompt name, or global policy (*)
        const exactMatch = policies.find((p: { prompt_name?: string; is_active?: boolean }) => p.prompt_name === prompt.name && p.is_active);
        const globalMatch = policies.find((p: { prompt_name?: string; is_active?: boolean }) => p.prompt_name === '*' && p.is_active);

        setApplicablePolicy(exactMatch || globalMatch || null);
      } catch (err) {
        console.error('Error loading policies:', err);
      } finally {
        setPolicyLoading(false);
      }
    };

    loadApplicablePolicy();
  }, [prompt?.name]);

  // Load all versions of this prompt for comparison (same name + same environment)
  useEffect(() => {
    const loadVersions = async () => {
      if (!prompt?.name || !prompt?.environment) return;

      try {
        // Search for prompts with exact name match in the same environment
        const data = await getPrompts({
          searchTerm: prompt.name,
          environment: prompt.environment,
          limit: 100  // Get all versions
        });
        const prompts = data.prompts || data.data || [];
        // Filter for exact name match
        const matchingPrompts = prompts
          .filter((p: { name?: string; environment?: string; version_number?: number; version?: number }) => p.name === prompt.name && p.environment === prompt.environment)
          .sort((a: { version_number?: number; version?: number }, b: { version_number?: number; version?: number }) => (b.version_number || b.version || 0) - (a.version_number || a.version || 0));
        // Extract unique version numbers
        const versions = matchingPrompts
          .map((p: { version_number?: number }) => p.version_number)
          .filter(Boolean);
        setAvailableVersions(Array.from(new Set(versions)));
        setVersionPrompts(matchingPrompts);

        // Also load version labels
        if (matchingPrompts.length > 0) {
          try {
            const labelsData = await getPromptLabels(matchingPrompts[0].id);
            setVersionLabels(labelsData.labels || []);
          } catch {
            // Labels endpoint may not exist yet during migration
          }
        }
      } catch (err) {
        console.error('Error loading versions:', err);
      }
    };

    loadVersions();
  }, [prompt?.name, prompt?.environment]);

  // Check for pending approval requests for this prompt
  useEffect(() => {
    const checkPendingApproval = async () => {
      if (!prompt?.id) return;

      try {
        const approvals = await getApprovals({ status: 'pending' });
        // Find approval for this specific prompt
        const pending = approvals.find(
          (a: { resource_id?: string; resource_type?: string }) => a.resource_id === prompt.id && a.resource_type === 'prompt'
        );
        setPendingApproval(pending || null);
      } catch (err) {
        console.error('Error checking pending approvals:', err);
      }
    };

    checkPendingApproval();
  }, [prompt?.id]);

  // Load release history for this prompt (by name, since prompt_id changes on promotion)
  useEffect(() => {
    const loadReleaseHistory = async () => {
      if (!prompt?.name) return;

      setReleaseHistoryLoading(true);
      try {
        const releases = await getReleaseHistory({ prompt_name: prompt.name, limit: 5 });
        setReleaseHistory(releases || []);
      } catch (err) {
        console.error('Error loading release history:', err);
        setReleaseHistory([]);
      } finally {
        setReleaseHistoryLoading(false);
      }
    };

    loadReleaseHistory();
  }, [prompt?.name]);

  // Load dynamic environments and promotion chain
  useEffect(() => {
    const loadEnvironments = async () => {
      try {
        // Fetch full environment definitions (with chain)
        const envData = await getEnvironments();
        if (envData?.environments?.length) {
          setEnvironments(envData.environments.map((e: { name: string }) => e.name));
          setEnvironmentDefs(envData.environments);
          setPromotionChain(envData.promotion_chain || null);
        }
      } catch {
        // Fallback to legacy endpoint
        try {
          const data = await listEnvironments();
          if (data?.environments?.length) {
            setEnvironments(data.environments);
          }
        } catch (err) {
          console.error('Error loading environments:', err);
        }
      }
    };
    loadEnvironments();
  }, []);

  const templateMeta = parseTemplate(isEditing ? editedData.template : prompt?.template || '');

  const handleSave = async () => {
    try {
      const result = await update(editedData);
      setIsEditing(false);

      // Show sync-aware feedback
      if (result?.bedrock_synced === true) {
        toast.success('Saved and synced to Bedrock');
      } else if (result?.version_created) {
        toast('Saved. Bedrock sync may be pending.', { icon: '\u26A0\uFE0F' });
      } else {
        toast.success('Prompt updated');
      }

      // Refresh Bedrock sync status widget
      bedrockSyncRef.current?.refresh?.();

      // If a new version was created, navigate to it
      if (result?.version_created && result?.prompt?.id) {
        navigate(`/ai-operations/prompts/${result.prompt.id}`);
      }
    } catch (err) {
      console.error('Error saving prompt:', err);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset edited data
    if (prompt) {
      setEditedData({
        template: prompt.template,
        description: prompt.description || '',
        tags: safeParse(prompt.tags, []),
        labels: safeParse(prompt.labels, []),
        config: safeParse(prompt.config, {})
      });
    }
  };

  const handleDelete = async () => {
    if (globalThis.confirm('Are you sure you want to delete this prompt? This action cannot be undone.')) {
      try {
        await remove();
        navigate('/ai-operations/registry?tab=prompts');
      } catch (err) {
        console.error('Error deleting prompt:', err);
      }
    }
  };

  const handleSetStable = async (versionPromptId: string) => {
    try {
      await assignVersionLabel(versionPromptId, 'stable');
      toast.success('Version marked as stable');
      // Refresh labels
      if (versionPrompts.length > 0) {
        const labelsData = await getPromptLabels(versionPrompts[0].id);
        setVersionLabels(labelsData.labels || []);
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to set stable label');
    }
  };

  const handleCloneOpen = () => {
    const defaultName = prompt ? `${prompt.name}-copy` : '';
    setCloneName(defaultName);
    setCloneError('');
    setShowCloneDialog(true);
  };

  const handleCloneConfirm = async () => {
    // Validate Bedrock naming: alphanumeric start, then alphanumeric/hyphens/underscores, 1-100 chars
    if (!/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,99}$/.test(cloneName)) {
      setCloneError('Name must be 1-100 characters: alphanumeric, hyphens, and underscores only. Must start with a letter or number.');
      return;
    }

    setCloning(true);
    setCloneError('');
    try {
      const result = await clonePrompt(promptId!, cloneName);
      setShowCloneDialog(false);
      toast.success(`Prompt cloned as "${cloneName}"`);
      navigate(`/ai-operations/prompts/${result.prompt.id}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Clone failed';
      setCloneError(msg);
    } finally {
      setCloning(false);
    }
  };

  const handleRenameOpen = () => {
    setRenameName(prompt?.name || '');
    setRenameError('');
    setShowRenameDialog(true);
  };

  const handleRenameConfirm = async () => {
    if (!/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,99}$/.test(renameName)) {
      setRenameError('Name must be 1-100 characters: alphanumeric, hyphens, and underscores only. Must start with a letter or number.');
      return;
    }
    if (renameName === prompt?.name) {
      setRenameError('New name must be different from the current name.');
      return;
    }

    setRenaming(true);
    setRenameError('');
    try {
      await renamePrompt(promptId!, renameName);
      setShowRenameDialog(false);
      toast.success(`Prompt renamed to "${renameName}"`);
      refresh();
      bedrockSyncRef.current?.refresh?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Rename failed';
      setRenameError(msg);
    } finally {
      setRenaming(false);
    }
  };

  const handleExport = () => {
    if (!prompt) return;

    const exportData = {
      name: prompt.name,
      template: prompt.template,
      description: prompt.description,
      prompt_type: prompt.prompt_type,
      tags: safeParse(prompt.tags, []),
      labels: safeParse(prompt.labels, []),
      config: safeParse(prompt.config, {})
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${prompt.name}-v${prompt.version}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePromote = async (promptId: string, targetEnvironment: string, releaseNotes: string | null) => {
    setPromoting(true);
    setPromotionResult(null);
    try {
      const result = await promotePrompt(promptId, targetEnvironment, releaseNotes);

      // Store the result to show in the modal
      setPromotionResult(result);

      // Only navigate on success
      if (result.status === 'success') {
        // After a short delay, navigate to the new prompt
        setTimeout(() => {
          navigate(`/ai-operations/prompts/${result.prompt.id}`);
          setShowPromoteModal(false);
          setPromotionResult(null);
        }, 1500);
      }
      // For pending_approval and blocked, modal stays open with result displayed
    } catch (err: unknown) {
      console.error('Error promoting prompt:', err);
      alert(`Failed to promote: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setPromoting(false);
    }
  };

  const handleRollback = async (promptId: string, targetVersion: number) => {
    setRollingBack(true);
    try {
      await rollbackPrompt(promptId, targetVersion);
      // Reload the page to show updated version
      globalThis.location.reload();
    } catch (err: unknown) {
      console.error('Error rolling back prompt:', err);
      alert(`Failed to rollback: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRollingBack(false);
    }
  };

  // Check if prompt can be promoted (dynamic: check if current env is in the promotion chain)
  const canPromote = promotionChain
    ? promotionChain.some((c: { from?: string }) => c.from === prompt?.environment)
    : prompt?.environment !== 'production';
  const canRollback = prompt?.version_number > 1;

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !prompt) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-300">
            {error || 'Prompt not found'}
          </p>
        </div>
      </div>
    );
  }

  let promptEnvClass: string;
  if (prompt.environment === 'production') {
    promptEnvClass = 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
  } else if (prompt.environment === 'staging') {
    promptEnvClass = 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400';
  } else {
    promptEnvClass = 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
  }

  let policyTriggerClass: string;
  if (applicablePolicy?.trigger === 'always') {
    policyTriggerClass = 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300';
  } else if (applicablePolicy?.trigger === 'staging_only') {
    policyTriggerClass = 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
  } else {
    policyTriggerClass = 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
  }

  let policyTriggerLabel: string;
  if (applicablePolicy?.trigger === 'always') {
    policyTriggerLabel = 'Always';
  } else if (applicablePolicy?.trigger === 'staging_only') {
    policyTriggerLabel = 'Staging';
  } else {
    policyTriggerLabel = 'Production';
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/ai-operations/registry?tab=prompts')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Prompts
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {prompt.name}
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Version {prompt.version} • {prompt.prompt_type} prompt
            </p>
          </div>

          {!isEditing && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setShowTestModal(true)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600
                         text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <TestTube className="w-4 h-4" />
                Test
              </button>
              <div className="flex items-center gap-2">
                {canPromote ? (
                  <button
                    onClick={() => setShowPromoteModal(true)}
                    className="flex items-center gap-2 px-4 py-2 border border-green-300 dark:border-green-600
                             text-green-700 dark:text-green-300 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20"
                  >
                    <ArrowUpCircle className="w-4 h-4" />
                    Promote
                  </button>
                ) : (
                  <span
                    className="flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-700
                             text-gray-400 dark:text-gray-500 rounded-lg cursor-not-allowed"
                    title="This prompt is already in production"
                  >
                    <CheckCircle className="w-4 h-4" />
                    In Production
                  </span>
                )}
                {pendingApproval && (
                  <Link
                    to={`/ai-operations/governance?tab=approvals`}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 dark:bg-yellow-900/30
                             text-yellow-800 dark:text-yellow-300 text-sm rounded-full
                             hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors"
                    title={`Pending approval to ${pendingApproval.to_environment}`}
                  >
                    <Clock className="w-3.5 h-3.5" />
                    <span>Pending Approval</span>
                  </Link>
                )}
              </div>
              {canRollback && (
                <button
                  onClick={() => setShowRollbackModal(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-orange-300 dark:border-orange-600
                           text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20"
                >
                  <RotateCcw className="w-4 h-4" />
                  Rollback
                </button>
              )}
              <button
                onClick={handleRenameOpen}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600
                         text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Edit className="w-4 h-4" />
                Rename
              </button>
              <button
                onClick={handleCloneOpen}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600
                         text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Copy className="w-4 h-4" />
                Clone
              </button>
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600
                         text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          )}

          {isEditing && (
            <div className="flex gap-2">
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600
                         text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || templateMeta.hasErrors}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg
                         hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Unsaved changes desync warning */}
      {isEditing && (editedData.template !== prompt.template || editedData.description !== (prompt.description || '')) && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Unsaved changes &mdash; Bedrock will be out of sync until you save
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Description
            </h2>
            {isEditing ? (
              <textarea
                value={editedData.description}
                onChange={(e) => setEditedData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <p className="text-gray-600 dark:text-gray-400">
                {prompt.description || 'No description provided'}
              </p>
            )}
          </div>

          {/* Template */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Template
            </h2>
            {isEditing ? (
              <PromptEditor
                value={editedData.template}
                onChange={(value) => setEditedData(prev => ({ ...prev, template: value }))}
                label=""
              />
            ) : (
              <div className="font-mono text-sm bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                <pre className="whitespace-pre-wrap text-gray-900 dark:text-gray-100">
                  {prompt.template}
                </pre>
              </div>
            )}
          </div>

          {/* Analytics */}
          {analytics && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Usage Analytics
                </h2>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Usage</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {analytics.total_usage || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Cost</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    ${(analytics.cost_analysis?.total_cost || 0).toFixed(4)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Avg Cost</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    ${(analytics.cost_analysis?.average_cost_per_invocation || 0).toFixed(4)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Related Traces */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Search className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Related Traces
                </h2>
              </div>
              {relatedTraces.length > 0 && (
                <Link
                  to={`/ai-operations/traces?promptName=${encodeURIComponent(prompt.name)}&promptVersion=${prompt.version_number}`}
                  className="text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 flex items-center gap-1"
                >
                  View all <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>

            {/* Filters */}
            <div className="flex gap-3 mb-4">
              <select
                value={traceFilters.version}
                onChange={(e) => setTraceFilters(f => ({ ...f, version: e.target.value }))}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Versions</option>
                {availableVersions.map(v => (
                  <option key={v} value={v}>
                    v{v}
                  </option>
                ))}
              </select>

              <select
                value={traceFilters.environment}
                onChange={(e) => setTraceFilters(f => ({ ...f, environment: e.target.value }))}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Environments</option>
                {environments.map(env => (
                  <option key={env} value={env}>
                    {env.charAt(0).toUpperCase() + env.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {tracesLoading && (
              <div className="flex items-center justify-center h-24">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
              </div>
            )}
            {!tracesLoading && tracesError && (
              <p className="text-sm text-red-600 dark:text-red-400">{tracesError}</p>
            )}
            {!tracesLoading && !tracesError && relatedTraces.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  No traces found for this prompt version.
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                  Traces will appear here when agents use this prompt with the SDK's trace attributes.
                </p>
              </div>
            )}
            {!tracesLoading && !tracesError && relatedTraces.length > 0 && (
              <div className="space-y-3">
                {relatedTraces.map((trace) => {
                  let traceOutcomeClass: string;
                  if (trace.outcome === 'success') {
                    traceOutcomeClass = 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300';
                  } else if (trace.outcome === 'error') {
                    traceOutcomeClass = 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300';
                  } else {
                    traceOutcomeClass = 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300';
                  }
                  return (
                  <Link
                    key={trace.id}
                    to={`/ai-operations/traces/${trace.id}`}
                    className="block p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {trace.agent_name || 'Unknown Agent'}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(trace.created_at || trace.start_time).toLocaleString()}
                          </span>
                          {trace.cost != null && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              ${Number(trace.cost).toFixed(4)}
                            </span>
                          )}
                          <span className={`px-1.5 py-0.5 rounded text-xs ${traceOutcomeClass}`}>
                            {trace.outcome || trace.status || 'unknown'}
                          </span>
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </div>
                  </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Version Comparison */}
          {availableVersions.length >= 2 && (
            <VersionComparisonSection
              promptId={prompt.id}
              versions={availableVersions}
              collapsed={true}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Metadata */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Metadata
            </h2>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-gray-600 dark:text-gray-400">Status</dt>
                <dd>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    prompt.is_active
                      ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                  }`}>
                    {prompt.is_active ? 'Active' : 'Inactive'}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-gray-600 dark:text-gray-400">Created</dt>
                <dd className="text-gray-900 dark:text-white">
                  {new Date(prompt.created_at).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-gray-600 dark:text-gray-400">Last Updated</dt>
                <dd className="text-gray-900 dark:text-white">
                  {new Date(prompt.updated_at).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-gray-600 dark:text-gray-400">Last Used</dt>
                <dd className="text-gray-900 dark:text-white">
                  {prompt.last_used_at ? new Date(prompt.last_used_at).toLocaleDateString() : 'Never'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-600 dark:text-gray-400">Bedrock</dt>
                <dd>
                  <SyncStatusBadge status={bedrockSyncStatus} />
                </dd>
              </div>
              <div>
                <dt className="text-gray-600 dark:text-gray-400">Project</dt>
                <dd>
                  <ProjectAssignmentSelect
                    currentProjectId={prompt.project_id}
                    onAssign={async (projectId) => {
                      await assignPromptProject(prompt.id, projectId);
                      refresh();
                    }}
                  />
                </dd>
              </div>
              <div>
                <dt className="text-gray-600 dark:text-gray-400">Workload</dt>
                <dd>
                  <WorkloadAssignmentSelect
                    currentWorkloadId={prompt.workload_id}
                    onAssign={async (workloadId) => {
                      await assignPromptWorkload(prompt.id, workloadId);
                      refresh();
                    }}
                  />
                </dd>
              </div>
            </dl>
          </div>

          {/* Bedrock Sync Status */}
          <BedrockSyncStatus
            ref={bedrockSyncRef}
            promptId={prompt.id}
            onExternalChanges={(_mapping) => {
            }}
            onSyncComplete={() => {
              refresh();
              toast.success('Prompt synced to Bedrock');
            }}
            onStatusChange={setBedrockSyncStatus}
          />

          {/* Version History */}
          {versionPrompts.length > 1 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <History className="w-5 h-5 text-purple-600" />
                Versions
              </h2>
              <div className="space-y-2">
                {versionPrompts.map((vp) => {
                  const isCurrent = vp.id === prompt.id;
                  const env = vp.environment?.value || vp.environment || '';
                  const isStable = versionLabels.some(
                    l => l.label === 'stable' && l.prompt_id === vp.id && l.environment === env
                  );
                  return (
                    <div key={vp.id} className={`flex items-center justify-between p-2 rounded-lg ${isCurrent ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">v{vp.version_number || vp.version}</span>
                        {isCurrent && (
                          <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">current</span>
                        )}
                        {isStable && (
                          <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300">stable</span>
                        )}
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                          vp.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                          {vp.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {!isStable && (
                          <button
                            onClick={() => handleSetStable(vp.id)}
                            className="text-xs text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 hover:underline"
                            title="Mark this version as stable"
                          >
                            Set Stable
                          </button>
                        )}
                        {!isCurrent && (
                          <Link
                            to={`/ai-operations/prompts/${vp.id}`}
                            className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
                          >
                            View
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Promotion Policy */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Promotion Policy
              </h2>
              <Link
                to="/ai-operations/governance?tab=policies"
                className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >
                Manage
              </Link>
            </div>

            {policyLoading && (
              <div className="flex items-center justify-center h-12">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              </div>
            )}
            {!policyLoading && applicablePolicy && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {applicablePolicy.name}
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${policyTriggerClass}`}>
                    {policyTriggerLabel}
                  </span>
                </div>

                <dl className="text-xs space-y-1">
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Min Pass Rate</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {((applicablePolicy.quality_gate_config?.min_pass_rate || 0) * 100).toFixed(0)}%
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Min Score</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {((applicablePolicy.quality_gate_config?.min_avg_score || 0) * 100).toFixed(0)}%
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Applies To</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {applicablePolicy.prompt_name === '*' ? 'All Prompts' : applicablePolicy.prompt_name}
                    </dd>
                  </div>
                </dl>

                <p className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                  Evaluation will run automatically on promotion.
                </p>
              </div>
            )}
            {!policyLoading && !applicablePolicy && (
              <div className="py-4">
                <div className="text-center mb-3">
                  <Info className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                    No policy applies to this prompt
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Promotions will proceed without automated evaluation.
                  </p>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-left">
                  <p className="text-xs text-blue-800 dark:text-blue-300 font-medium mb-2">
                    Why create a policy?
                  </p>
                  <ul className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
                    <li className="flex items-start gap-1.5">
                      <span className="text-blue-500 mt-0.5">•</span>
                      <span>Run evaluations automatically before promotion</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <span className="text-blue-500 mt-0.5">•</span>
                      <span>Enforce quality gates (min pass rate, score)</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <span className="text-blue-500 mt-0.5">•</span>
                      <span>Require approval for production deployments</span>
                    </li>
                  </ul>
                </div>

                <Link
                  to="/ai-operations/governance"
                  className="inline-flex items-center gap-1.5 mt-3 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                >
                  <Plus className="w-3.5 h-3.5" /> Create a Policy
                </Link>
              </div>
            )}
          </div>

          {/* Promotion History */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <History className="w-5 h-5 text-green-600" />
                Promotion History
              </h2>
              {releaseHistory.length > 0 && (
                <Link
                  to="/ai-operations/governance?tab=releases"
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-1"
                >
                  View All <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>

            {releaseHistoryLoading && (
              <div className="flex items-center justify-center h-20">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              </div>
            )}
            {!releaseHistoryLoading && releaseHistory.length === 0 && (
              <div className="text-center py-6">
                <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-3">
                  <Rocket className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No promotions yet</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Currently in <span className={`font-medium px-1.5 py-0.5 rounded ${promptEnvClass}`}>{prompt.environment}</span>
                </p>
              </div>
            )}
            {!releaseHistoryLoading && releaseHistory.length > 0 && (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-[9px] top-3 bottom-3 w-0.5 bg-gradient-to-b from-green-400 via-yellow-400 to-blue-400 dark:from-green-500 dark:via-yellow-500 dark:to-blue-500 rounded-full" />

                <div className="space-y-4">
                  {releaseHistory.slice(0, 4).map((release, index) => {
                    const envConfig: Record<string, { bg: string; ring: string; badge: string; Icon: React.ComponentType<{ className?: string }> }> = {
                      production: {
                        bg: 'bg-green-500',
                        ring: 'ring-green-100 dark:ring-green-900/50',
                        badge: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700',
                        Icon: Rocket
                      },
                      staging: {
                        bg: 'bg-yellow-500',
                        ring: 'ring-yellow-100 dark:ring-yellow-900/50',
                        badge: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700',
                        Icon: FiActivity
                      },
                      development: {
                        bg: 'bg-blue-500',
                        ring: 'ring-blue-100 dark:ring-blue-900/50',
                        badge: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700',
                        Icon: FiCode
                      }
                    };
                    const config = envConfig[release.environment] ?? envConfig.development;
                    const isLatest = index === 0;

                    return (
                      <div key={release.id} className="relative flex items-start gap-3 pl-0">
                        {/* Timeline dot */}
                        <div className={`relative z-10 flex-shrink-0 w-[18px] h-[18px] rounded-full ${config.bg} ring-4 ${config.ring} ${isLatest ? 'scale-110' : ''}`}>
                          {isLatest && (
                            <div className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ backgroundColor: 'currentColor' }} />
                          )}
                        </div>

                        {/* Content */}
                        <div className={`flex-1 min-w-0 pb-1 ${isLatest ? 'bg-gray-50 dark:bg-gray-700/30 -ml-1 pl-2 pr-3 py-2 rounded-lg border border-gray-100 dark:border-gray-700' : ''}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${config.badge}`}>
                                <config.Icon className="w-3 h-3" />
                                <span className="capitalize">{release.environment}</span>
                              </span>
                              <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded">
                                v{release.version_number}
                              </span>
                              {release.release_type === 'promotion' && release.approval_request_id && (
                                <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-0.5" title="Approved promotion">
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  Approved
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                              {new Date(release.released_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: new Date(release.released_at).getFullYear() === new Date().getFullYear() ? undefined : 'numeric'
                              })}
                            </span>
                          </div>

                          {/* Release meta info */}
                          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span>by {formatUserName(release.released_by)}</span>
                            {release.pass_rate != null && (
                              <>
                                <span className="text-gray-300 dark:text-gray-600">•</span>
                                <span className={release.pass_rate >= 0.8 ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}>
                                  {(release.pass_rate * 100).toFixed(0)}% pass rate
                                </span>
                              </>
                            )}
                          </div>

                          {release.release_notes && (
                            <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-300 line-clamp-1 italic">
                              "{release.release_notes}"
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {releaseHistory.length > 4 && (
                  <Link
                    to="/ai-operations/governance?tab=releases"
                    className="mt-4 flex items-center justify-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 py-2.5 border-t border-gray-100 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-b-lg transition-colors"
                  >
                    View all {releaseHistory.length} releases
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Tags */}
          {prompt.tags && safeParse(prompt.tags, []).length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Tags
              </h2>
              <div className="flex flex-wrap gap-2">
                {safeParse(prompt.tags, []).map((tag: string) => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 rounded text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Labels */}
          {prompt.labels && safeParse(prompt.labels, []).length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Labels
              </h2>
              <div className="flex flex-wrap gap-2">
                {safeParse(prompt.labels, []).map((label: string) => (
                  <span
                    key={label}
                    className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 rounded text-sm"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Promotion Modal */}
      <PromotionModal
        isOpen={showPromoteModal}
        onClose={() => {
          setShowPromoteModal(false);
          setPromotionResult(null);
        }}
        prompt={prompt as any}
        onPromote={handlePromote}
        isLoading={promoting}
        promotionResult={promotionResult}
        applicablePolicy={applicablePolicy}
        promotionChain={promotionChain}
        environmentDefs={environmentDefs}
      />

      {/* Rollback Modal */}
      <RollbackModal
        isOpen={showRollbackModal}
        onClose={() => setShowRollbackModal(false)}
        prompt={prompt as any}
        onRollback={handleRollback}
        isLoading={rollingBack}
      />

      {/* Test Prompt Modal */}
      <TestPromptModal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        prompt={prompt as any}
      />

      {/* Clone Dialog */}
      {showCloneDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Clone Prompt</h3>
            <div className="mb-4">
              <label htmlFor="new-prompt-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                New Prompt Name
              </label>
              <input id="new-prompt-name"
                type="text"
                value={cloneName}
                onChange={(e) => {
                  setCloneName(e.target.value);
                  setCloneError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleCloneConfirm()}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="my-prompt-v2"
                autoFocus
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Alphanumeric, hyphens, and underscores only. No spaces. Must start with a letter or number.
              </p>
              {cloneError && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{cloneError}</p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCloneDialog(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300
                         dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                disabled={cloning}
              >
                Cancel
              </button>
              <button
                onClick={handleCloneConfirm}
                disabled={cloning || !cloneName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                         disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {cloning ? 'Cloning...' : 'Clone'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRenameDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Rename Prompt</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              This will rename all versions across all environments.
            </p>
            <div className="mb-4">
              <label htmlFor="new-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                New Name
              </label>
              <input id="new-name"
                type="text"
                value={renameName}
                onChange={(e) => {
                  setRenameName(e.target.value);
                  setRenameError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleRenameConfirm()}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="my-prompt-name"
                autoFocus
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Alphanumeric, hyphens, and underscores only. No spaces. Must start with a letter or number.
              </p>
              {renameError && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{renameError}</p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRenameDialog(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300
                         dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                disabled={renaming}
              >
                Cancel
              </button>
              <button
                onClick={handleRenameConfirm}
                disabled={renaming || !renameName.trim() || renameName === prompt?.name}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                         disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {renaming ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptDetailPage;
